import { Control, Controller, ControllerProps, FieldError, FieldPath, FieldValues } from "react-hook-form";
import { TextField, TextFieldProps } from "@mui/material";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { convertStringToNumber, uppercaseFirstLetter } from "@/main/services/utilsString";

import { useStyles } from "./styles";
import { getSeparatorDecimalByCulture } from "@/main/services/utilsNumber";

type IControllerProps = Omit<ControllerProps, "render" | "name">;

export type TextInputProps<T extends FieldValues> = {
	errorMessageCustom?: string;
	name: FieldPath<T>;
	control: Control<T>;
	controllerProps?: IControllerProps;
	hasOnlyNumber?: boolean;
	hasOnlyNumberInteger?: boolean;
	rangeOnlyNumber?: { min: number; max: number } | null;
	numberOfDecimals?: number | null;
	disabledMessage?: boolean;
	hideValue?: boolean;
	removeWhitespace?: boolean;
	onChangeCustom?: (value: string) => void;
	onBlurCustom?: (value: string) => void;
};

type KogInputTextControlledNewProps<T extends FieldValues> = TextInputProps<T> & TextFieldProps;

const KogInputTextControlledNew = <T extends FieldValues>({
	name,
	control,
	errorMessageCustom,
	controllerProps,
	hasOnlyNumber = false,
	hasOnlyNumberInteger = false,
	rangeOnlyNumber = null,
	numberOfDecimals = null,
	disabledMessage = false,
	hideValue = false,
	removeWhitespace = false,
	onChangeCustom = () => {},
	onBlurCustom = () => {},
	...props
}: KogInputTextControlledNewProps<T>) => {
	const styles = useStyles({});
	const renderErrorMessages = (error: FieldError | undefined) => {
		const translatesUpperCase = [getTranslateByKey("information.rfc"), getTranslateByKey("information.rim")]; // Add here the translates that must be uppercase
		// Common Messages
		const fieldMessage = getTranslateByKey("information.field");
		const thisFieldMessage = getTranslateByKey("information.thisField");
		const labelMessage = (props.label as string) || thisFieldMessage;
		// Specific Messages
		const requiredBaseMessage = getTranslateByKey("information.isRequired", true).replace(
			"{0}",
			`${String(props.label || fieldMessage)}`,
		);
		const requiredFormattedMessage = translatesUpperCase.includes(labelMessage)
			? requiredBaseMessage
			: uppercaseFirstLetter(requiredBaseMessage);

		const minLengthMessage = `${labelMessage}
      ${getTranslateByKey("information.mustHaveAtLeast")}
      ${controllerProps?.rules?.minLength}
      ${getTranslateByKey("information.digits")}`;

		if (!error) return null;
		switch (error.type) {
			case "required":
				return requiredFormattedMessage;
			case "minLength":
				return minLengthMessage;
			default:
				return error.message;
		}
	};

	function handleValueChange(valueProp: string) {
		const value = removeWhitespace ? valueProp.replace(/\s/g, "") : valueProp;
		let valueToValidate = value;
		const { separatorDecimal } = getSeparatorDecimalByCulture();

		valueToValidate = convertStringToNumber(value, {
			hasOnlyNumber,
			hasOnlyNumberInteger,
		});
		if (!isNullUndefined(rangeOnlyNumber) && (hasOnlyNumber || hasOnlyNumberInteger)) {
			const numericValue =
				value && hasOnlyNumber && hasOnlyNumberInteger
					? hasOnlyNumber
						? Number(parseFloat(value.replace(",", ".")))
						: Number(value)
					: value;
			if (
				numericValue != "" &&
				(Number(numericValue) < rangeOnlyNumber.min || Number(numericValue) > rangeOnlyNumber.max)
			) {
				return convertStringToNumber(value.slice(0, -1), {
					hasOnlyNumber,
					hasOnlyNumberInteger,
				});
			}
			valueToValidate = String(numericValue);
		}
		if (hasOnlyNumber && !isNullUndefined(numberOfDecimals)) {
			const decimalPart = valueToValidate.split(separatorDecimal)[1];
			if (decimalPart && decimalPart.length > numberOfDecimals) {
				valueToValidate = `${valueToValidate.split(separatorDecimal)[0]}${separatorDecimal}${decimalPart.slice(0, -1)}`;
			}
		}
		return convertStringToNumber(valueToValidate, {
			hasOnlyNumber,
			hasOnlyNumberInteger,
		});
	}

	const customControllerProps: IControllerProps = {
		...controllerProps,
		...{
			rules: {
				...controllerProps?.rules,
				required: props.required || controllerProps?.rules?.required,
			},
		},
	};

	return (
		<Controller
			name={name}
			control={control as Control}
			{...customControllerProps}
			render={({ field: { ref, ...field }, fieldState: { error } }) => (
				<TextField
					fullWidth
					required={Boolean(customControllerProps?.rules?.required)}
					size="small"
					variant="filled"
					error={!!error}
					helperText={!disabledMessage && (errorMessageCustom || renderErrorMessages(error))}
					className={`${styles.inputText} ignore-click ${props.id}-container ${props.className}`}
					InputProps={{ ...props.InputProps }}
					inputProps={props.inputProps ? props.inputProps : { maxLength: 100 }}
					inputRef={ref}
					{...field}
					{...props}
					onChange={(e) => {
						const validateValue = handleValueChange(e.target.value);

						field.onChange(validateValue);
						onChangeCustom(validateValue);
					}}
					onBlur={(e) => {
						const validateValue = handleValueChange(e.target.value);

						field.onBlur();
						onBlurCustom(validateValue);
					}}
					value={hideValue ? "" : (field.value ?? "")}
				/>
			)}
		/>
	);
};

export default KogInputTextControlledNew;
